const React = require('react');
const { useField } = require('hooks/useFormPrimitives/FieldContext');

const internals = {};

internals.baseRolePermissions = {
    canEditAge: false,
    canEditMajor: false,
    canEditHomeTown: false,
    canEditCareerAspiration: false,
    canEditProfession: false,
    canEditDepartment: false,
    canEditBadge: false,
    canEditTitle: false,
    canEditIncomingClass: false,
    canEditGraduatingClass: false,
    canEditBio: false,
    canEditLiveOnCampus: false,
    canEditTransfer: false,
    canEditVeteran: false,
    canEditOnline: false,
    canEditParent: false,
    canEditOpenSocial: false,
    canEditTimeStatus: false,
    canEditStudentName: false,
    canEditStudentBio: false,
    canEditYearHired: false,
    canEditOffice: false,
    canEditWorkRemote: false,
    includeEthnicity: false,
    profilePicRequired: false,
    includePronouns: false,
    canEditIntroExtro: false,
    defaultPhoto: ''
};

internals.buildRolePermissions = (view) => {

    if (view === 'student') {
        return {
            ...internals.baseRolePermissions,
            canEditAge: true,
            canEditMajor: true,
            canEditHomeTown: true,
            canEditCareerAspiration: true,
            canEditProfession: true,
            canEditIncomingClass: true,
            canEditGraduatingClass: true,
            canEditBio: true,
            canEditLiveOnCampus: true,
            canEditTransfer: true,
            canEditVeteran: true,
            canEditOnline: true,
            canEditOpenSocial: true,
            canEditTimeStatus: true,
            includeEthnicity: true,
            includePronouns: true,
            canEditIntroExtro: true
        };
    }

    if (view === 'staff') {
        return {
            ...internals.baseRolePermissions,
            canEditDepartment: true,
            canEditOffice: true,
            canEditYearHired: true,
            canEditWorkRemote: true,
            canEditTitle: true,
            canEditBio: true,
            canEditHomeTown: true
        };
    }

    if (view === 'superuser') {
        return {
            ...internals.buildRolePermissions('student'),
            ...internals.buildRolePermissions('staff'),
            canEditBadge: true
        };
    }

    if (view === 'parent') {
        return {
            ...internals.baseRolePermissions,
            canEditStudentName: true,
            canEditStudentBio: true,
            canEditMajor: true,
            canEditTransfer: true,
            canEditIncomingClass: true,
            canEditGraduatingClass: true,
            canEditOnline: true
        };
    }

    if (view === 'teams') {
        return {
            ...internals.buildRolePermissions('staff'),
            canEditLiveOnCampus: true
        };
    }

    return {
        ...internals.baseRolePermissions
    };
};

internals.buildReferenceRolePermissions = () => ({
    ...internals.baseRolePermissions,
    canEditDepartment: true,
    canEditBadge: true,
    canEditTitle: true,
    canEditHomeTown: true,
    canEditBio: true,
    canEditVeteran: true,
    canEditParent: true
});

internals.buildSignupDetails = (view, fixture) => {
    const rolePermissions = fixture === 'edit-desktop-reference'
        ? internals.buildReferenceRolePermissions()
        : internals.buildRolePermissions(view);
    const useEmptyNamesFixture = fixture === 'empty-names';

    return {
        firstName: useEmptyNamesFixture ? '' : (fixture === 'edit-desktop-reference' ? 'notiftest' : 'Casey'),
        lastName: useEmptyNamesFixture ? '' : (fixture === 'edit-desktop-reference' ? 'notiftest' : 'Rivera'),
        birthdate: rolePermissions.canEditAge ? new Date('2000-01-15T00:00:00.000Z') : null,
        pronouns: '',
        gender: fixture === 'edit-desktop-reference' ? 'decline' : 'woman',
        ethnicity: rolePermissions.includeEthnicity ? 'latine' : 'decline',
        departmentId: rolePermissions.canEditDepartment ? 1 : '',
        officeId: rolePermissions.canEditOffice ? 1 : '',
        yearHiredId: rolePermissions.canEditYearHired ? 1 : '',
        workRemote: rolePermissions.canEditWorkRemote,
        title: rolePermissions.canEditTitle ? (fixture === 'edit-desktop-reference' ? '' : 'Program Director') : '',
        majorId: rolePermissions.canEditMajor ? 1 : null,
        incomingClass: rolePermissions.canEditIncomingClass ? '2028' : '',
        graduatingClass: rolePermissions.canEditGraduatingClass ? '2026' : '',
        career: rolePermissions.canEditCareerAspiration ? 'Software Engineer' : '',
        profession: rolePermissions.canEditProfession ? 'Advisor' : '',
        bio: rolePermissions.canEditBio ? (fixture === 'edit-desktop-reference' ? '' : 'Helping people connect.') : '',
        hometown: rolePermissions.canEditHomeTown ? {
            id: null,
            placeId: null,
            name: fixture === 'edit-desktop-reference' ? 'St. Louis, MO, USA' : 'New Orleans',
            onCampus: null
        } : {
            id: null,
            placeId: null,
            name: null,
            onCampus: null
        },
        housing: rolePermissions.canEditLiveOnCampus ? {
            id: null,
            placeId: null,
            name: 'Maple Hall',
            onCampus: false
        } : {
            id: null,
            placeId: null,
            name: null,
            onCampus: false
        },
        transfer: rolePermissions.canEditTransfer ? {
            id: 'transfer-university',
            name: null
        } : {
            id: null,
            name: null
        },
        veteran: rolePermissions.canEditVeteran ? (fixture === 'edit-desktop-reference' ? false : true) : false,
        onlineStudent: rolePermissions.canEditOnline ? true : false,
        parent: rolePermissions.canEditParent ? (fixture === 'edit-desktop-reference' ? false : true) : false,
        openToSocial: rolePermissions.canEditOpenSocial ? true : false,
        fullTime: rolePermissions.canEditTimeStatus ? true : false,
        introExtroLevel: rolePermissions.canEditIntroExtro ? 3 : null,
        studentName: view === 'parent' ? 'Jordan Rivera' : '',
        studentBio: view === 'parent' ? 'Interested in robotics.' : '',
        interests: [101],
        passionInterests: [101],
        preferences: {}
    };
};

internals.buildSharedProps = ({
    view,
    fixture,
    onInvalidSubmit = () => null,
    onSubmit = async () => ({
        payload: {
            ok: true
        }
    }),
    onSubmitNoRedirect = async () => null,
    saveProfilePicture = async () => true
} = {}) => ({
    badges: [
        {
            name: 'Faculty Fellow',
            label: 'Faculty Fellow'
        }
    ],
    componentsByKey: internals.getComponentsByKey(fixture),
    componentsByName: internals.getComponentsByName(fixture),
    componentsByType: internals.getComponentsByType(fixture),
    defaultInterestId: undefined,
    departments: [
        { id: 1, name: 'Engineering' }
    ],
    fixture,
    graduatingClassList: ['2026'],
    hideJobAlerts: true,
    includePreferences: fixture === 'edit-desktop-reference',
    incomingClassList: ['2028'],
    interestsLoading: false,
    isCompany: view === 'teams',
    isCommunity: false,
    isSchoolOnline: false,
    location: {
        search: ''
    },
    majors: [
        { id: 1, name: 'Computer Science' }
    ],
    officesList: [
        { id: 1, name: 'Main Office' }
    ],
    offCampusHousingList: [
        { id: 1, placeId: 'housing-1', name: 'Maple Hall' }
    ],
    onCampusHousingList: [
        { id: 2, placeId: 'housing-2', name: 'Oak Hall' }
    ],
    onInvalidSubmit,
    onSubmit,
    onSubmitNoRedirect,
    officesLoading: false,
    preferencesFieldsToShow: fixture === 'edit-desktop-reference' ? ['sms'] : undefined,
    preferencesHeadersToShow: fixture === 'edit-desktop-reference' ? ['communications'] : undefined,
    preferencesLoading: false,
    role: {
        id: 1,
        name: view,
        label: view
    },
    rolePermissions: fixture === 'edit-desktop-reference'
        ? internals.buildReferenceRolePermissions()
        : internals.buildRolePermissions(view),
    savePreferencesProgress: () => null,
    saveProfilePicture,
    saveProfileProgress: () => null,
    schema: {},
    showNotification: () => null,
    signupDetails: internals.buildSignupDetails(view, fixture),
    transfers: [
        { id: 'transfer-university', name: 'Transfer University' }
    ],
    useProfanityFilter: false,
    yearsHiredList: [
        { id: 1, year: 2024 }
    ],
    yearsHiredLoading: false
});

internals.getHometownLabel = (formProps, field) => (
    formProps.isCompany || formProps.isCommunity || formProps.isSchoolOnline
        ? 'Where I Grew Up'
        : (field.label || 'Hometown')
);

internals.getHousingLabel = (formProps) => (
    formProps.isCompany || formProps.isCommunity || formProps.isSchoolOnline
        ? 'Where I Live'
        : 'Off-campus location:'
);

internals.buildPlaceValue = (name, onCampus = null) => ({
    id: null,
    placeId: null,
    name: name || null,
    onCampus
});

internals.HarnessInterestsField = function HarnessInterestsField(props) {

    const {
        field,
        fieldName
    } = props;
    const interestsField = useField(fieldName);
    const passionField = useField(field.passionFieldName || 'passionInterests');
    const formProps = interestsField.formProps || {};
    const values = interestsField.getValue() || [];

    if (formProps.fixture === 'edit-desktop-reference') {
        return <div>
            <a href='#edit-my-interests'>Edit My Interests</a>
        </div>;
    }

    const hasInterests = values.length > 0;

    return <div>
        <div style={{ marginBottom: 8 }}>{field.label || 'Interests'}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            {hasInterests ? <div
                data-testid='harness-interests-value'
                style={{
                    borderRadius: 16,
                    padding: '4px 12px',
                    background: '#f0ebff',
                    color: '#3b1cb5',
                    fontSize: 14
                }}
            >
                Interest {values.join(', ')}
            </div> : <div data-testid='harness-interests-value' style={{ color: '#666', fontSize: 14 }}>
                No interests selected
            </div>}
            <button
                type='button'
                onClick={() => {

                    interestsField.setValue([101]);
                    passionField.setValue([101]);
                }}
                style={{
                    borderRadius: 16,
                    border: '1px solid #5f2ebf',
                    background: '#fff',
                    color: '#5f2ebf',
                    fontSize: 13,
                    padding: '4px 12px'
                }}
            >
                Add interest
            </button>
        </div>
    </div>;
};

internals.HarnessHometownField = function HarnessHometownField(props) {

    const {
        field,
        fieldName
    } = props;
    const hometownField = useField(fieldName);
    const hometownTextField = useField(field.textFieldName || 'hometownText');
    const formProps = hometownField.formProps;
    const label = internals.getHometownLabel(formProps, field);
    const value = hometownTextField.getValue() || '';

    return <label>
        {label}
        <input
            aria-label={label}
            value={value}
            onChange={(event) => {

                const nextValue = event.target.value;
                hometownTextField.setValue(nextValue, undefined, event);
                hometownField.setValue(internals.buildPlaceValue(nextValue), undefined, event);
            }}
        />
    </label>;
};

internals.HarnessHousingField = function HarnessHousingField(props) {

    const {
        field,
        fieldName
    } = props;
    const housingField = useField(fieldName);
    const housingTextField = useField(field.textFieldName || 'housingText');
    const formProps = housingField.formProps;
    const label = internals.getHousingLabel(formProps);
    const value = housingTextField.getValue() || '';

    return <label>
        {label}
        <input
            aria-label={label}
            value={value}
            onChange={(event) => {

                const nextValue = event.target.value;
                housingTextField.setValue(nextValue, undefined, event);
                housingField.setValue(internals.buildPlaceValue(nextValue, false), undefined, event);
            }}
        />
    </label>;
};

internals.componentsByKey = {
    interests: internals.HarnessInterestsField,
    hometown: internals.HarnessHometownField,
    housing: internals.HarnessHousingField
};

internals.componentsByName = {};

internals.componentsByType = {
    CampusPlaceField: internals.HarnessHousingField,
    InterestsInputField: internals.HarnessInterestsField,
    PlaceField: internals.HarnessHometownField
};

internals.getComponentsByKey = (fixture) => {

    if (fixture === 'real-place-fields') {
        return {
            interests: internals.HarnessInterestsField
        };
    }

    return internals.componentsByKey;
};

internals.getComponentsByName = () => internals.componentsByName;

internals.getComponentsByType = (fixture) => {

    if (fixture === 'real-place-fields') {
        return {
            InterestsInputField: internals.HarnessInterestsField
        };
    }

    return internals.componentsByType;
};

const scenarioExpectations = {
    student: {
        visible: ['Profile photo', 'First name', 'Last name', 'Age', 'Pronouns', 'Ethnicity', 'Major', 'Incoming class', 'Graduating class', 'Career aspiration', 'Profession', 'Hometown', 'Off-campus location:', 'Transfer student', 'Veteran', 'Online student', 'Full time', 'Introvert / extrovert level', 'Interests'],
        hidden: ['Department', 'Office', 'Year hired', 'Works remotely', 'Student name']
    },
    staff: {
        visible: ['Profile photo', 'First name', 'Last name', 'Gender', 'Department', 'Office', 'Year hired', 'Works remotely', 'Title', 'Hometown', 'Bio', 'Interests'],
        hidden: ['Major', 'Incoming class', 'Graduating class', 'Transfer student', 'Student name']
    },
    superuser: {
        visible: ['Profile photo', 'Badge', 'Department', 'Office', 'Year hired', 'Works remotely', 'Title', 'Major', 'Incoming class', 'Graduating class', 'Career aspiration', 'Profession', 'Hometown', 'Off-campus location:', 'Transfer student', 'Interests'],
        hidden: ['Student name']
    },
    observer: {
        visible: ['Profile photo', 'First name', 'Last name', 'Gender', 'Interests'],
        hidden: ['Department', 'Major', 'Transfer student', 'Student name', 'Pronouns']
    },
    parent: {
        visible: ['Profile photo', 'First name', 'Last name', 'Your student', 'Student name', 'Student bio', 'Major', 'Transfer student', 'Incoming class', 'Graduating class', 'Online student', 'Interests'],
        hidden: ['Department', 'Office', 'Works remotely']
    },
    teams: {
        visible: ['Profile photo', 'First name', 'Last name', 'Department', 'Title', 'Where I Grew Up', 'Where I Live', 'Interests'],
        hidden: ['Major', 'Incoming class', 'Student name']
    }
};

exports.buildUserInfoProps = ({
    view = 'student',
    fixture,
    handlers = {}
} = {}) => ({
    ...internals.buildSharedProps({
        view,
        fixture,
        ...handlers
    }),
    isAuthenticated: false,
    isParent: view === 'parent',
    onGoBack: () => null,
    signupContext: {
        joinedSchoolIds: []
    }
});

exports.buildEditPageProps = ({
    view = 'student',
    fixture,
    handlers = {},
    history = {
        push: () => null
    }
} = {}) => ({
    ...internals.buildSharedProps({
        view,
        fixture,
        ...handlers
    }),
    history
});

exports.getScenarioExpectations = (view) => scenarioExpectations[view] || scenarioExpectations.observer;
exports.getScenarioViews = () => Object.keys(scenarioExpectations);
